/**
 * Ticket SLA / waiting-time helpers.
 *
 * Dependency-free and shared by the ticket list rows, the mobile cards and the
 * details panel — the same "one shared helper module" pattern as
 * src/lib/placementUtils.ts. Do NOT duplicate this maths inline.
 *
 * The backend has no SLA calculator, so the clock is derived from fields every
 * ticket already carries: `createdAt`, `lastCommentAt` and `lastCommentBy`.
 * The window is the team's first-response obligation: it runs from the
 * applicant's most recent message (or from ticket creation, before anyone has
 * replied) and STOPS counting once the ball is in the applicant's court — an
 * admin who replied an hour ago must not show as "breaching".
 */

import type { Ticket, TicketPriority, TicketStatus } from '../../../types';

/** First-response targets by priority (hours). Single source of truth. */
export const SLA_TARGET_HOURS: Record<TicketPriority, number> = {
  urgent: 4,
  high: 8,
  medium: 24,
  low: 48,
};

/** Fraction of the target at which the row turns amber. */
export const SLA_WARNING_RATIO = 0.75;

export type SlaState = 'ok' | 'warning' | 'breach' | 'waiting' | 'none';

export interface TicketSla {
  state: SlaState;
  /** Minutes the team has owed a reply (0 when waiting on the applicant). */
  waitingMinutes: number;
  targetMinutes: number;
  /** targetMinutes - waitingMinutes; negative once breached. */
  remainingMinutes: number;
  /** Short badge text, e.g. "Waiting 2h · SLA in 1h". */
  label: string;
  /** Longer tooltip text. */
  title: string;
}

const TERMINAL_STATUSES: TicketStatus[] = ['resolved', 'closed'];

/** "45m" / "2h" / "1d 4h" */
export function formatSlaDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainder = hours % 24;
  return remainder ? `${days}d ${remainder}h` : `${days}d`;
}

function elapsedMinutes(iso: string | undefined | null, now: number): number | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;
  return (now - parsed) / 60000;
}

export function getSlaTargetMinutes(priority: TicketPriority): number {
  return (SLA_TARGET_HOURS[priority] ?? SLA_TARGET_HOURS.medium) * 60;
}

/**
 * Resolve the SLA state of a ticket. Returns `state: 'none'` for resolved /
 * closed tickets (with a "Resolved in…" label when we can compute it).
 */
export function getTicketSla(ticket: Ticket, now: number = Date.now()): TicketSla {
  const targetMinutes = getSlaTargetMinutes(ticket.priority);
  const none: TicketSla = {
    state: 'none',
    waitingMinutes: 0,
    targetMinutes,
    remainingMinutes: targetMinutes,
    label: '',
    title: 'No response clock runs on a resolved or closed ticket.',
  };

  if (TERMINAL_STATUSES.includes(ticket.status)) {
    const endMinutes = elapsedMinutes(ticket.resolvedAt || ticket.closedAt, now);
    const startMinutes = elapsedMinutes(ticket.createdAt, now);
    if (endMinutes != null && startMinutes != null) {
      const lifetime = Math.max(0, startMinutes - endMinutes);
      return {
        ...none,
        label: `Resolved in ${formatSlaDuration(lifetime)}`,
        title: `This ticket was resolved ${formatSlaDuration(endMinutes)} ago.`,
      };
    }
    return none;
  }

  // The team replied last → the clock is paused, the applicant owes us.
  if (ticket.lastCommentBy === 'admin' && ticket.lastCommentAt) {
    const idle = Math.max(0, elapsedMinutes(ticket.lastCommentAt, now) ?? 0);
    return {
      ...none,
      state: 'waiting',
      waitingMinutes: 0,
      label: `Waiting on applicant for ${formatSlaDuration(idle)}`,
      title: `Our last reply was ${formatSlaDuration(idle)} ago. The response clock restarts when the applicant replies. Measured in elapsed calendar time — business hours and an office timezone are not applied.`,
    };
  }

  // Applicant spoke last (or nobody has spoken yet) → we owe a first response.
  const since = ticket.lastCommentAt || ticket.createdAt;
  const waitingMinutes = Math.max(0, elapsedMinutes(since, now) ?? 0);
  const remainingMinutes = targetMinutes - waitingMinutes;
  const state: SlaState = remainingMinutes <= 0
    ? 'breach'
    : waitingMinutes >= targetMinutes * SLA_WARNING_RATIO
      ? 'warning'
      : 'ok';

  const label = state === 'breach'
    ? `SLA breached ${formatSlaDuration(-remainingMinutes)} ago`
    : `Waiting ${formatSlaDuration(waitingMinutes)} · SLA in ${formatSlaDuration(remainingMinutes)}`;

  return {
    state,
    waitingMinutes,
    targetMinutes,
    remainingMinutes,
    label,
    title: `${ticket.priority} priority · first-response target ${formatSlaDuration(targetMinutes)} · owed for ${formatSlaDuration(waitingMinutes)}. Measured in elapsed calendar time — business hours and an office timezone are not applied.`,
  };
}

/**
 * Sort weight for "needs attention first": breached > warning > ok > waiting-on-
 * applicant > no clock. Stable enough to sort the queue without extra state.
 */
export function slaSortWeight(ticket: Ticket, now: number = Date.now()): number {
  const { state } = getTicketSla(ticket, now);
  switch (state) {
    case 'breach': return 0;
    case 'warning': return 1;
    case 'ok': return 2;
    case 'waiting': return 3;
    default: return 4;
  }
}

export const SLA_STATE_STYLES: Record<SlaState, { bg: string; text: string; dot: string; label: string }> = {
  breach: { bg: 'var(--color-danger-bg, #FBEAEA)', text: 'var(--color-danger, #C13F3F)', dot: 'var(--color-danger, #C13F3F)', label: 'SLA breached' },
  warning: { bg: 'var(--color-warning-bg, #FDF3E3)', text: 'var(--color-warning, #B7791F)', dot: 'var(--color-warning, #B7791F)', label: 'Approaching SLA' },
  ok: { bg: 'var(--gray-50, #F5F6F8)', text: 'var(--gray-600, #5B6270)', dot: 'var(--gray-400, #8A9099)', label: 'Within SLA' },
  waiting: { bg: 'var(--gray-50, #F5F6F8)', text: 'var(--gray-500, #8A9099)', dot: 'var(--gray-300, #B6BAC1)', label: 'Waiting on applicant' },
  none: { bg: 'var(--gray-50, #F5F6F8)', text: 'var(--gray-500, #8A9099)', dot: 'var(--gray-300, #B6BAC1)', label: 'No clock' },
};
